import React, { Component } from 'react';
import Carousel from 'brastrap/common/carousel/Carousel';
import EditorialContainer from 'brastrap/editorial/editorial-container/EditorialContainer';
import ProductGalleryMainCarousel from './ProductGalleryMainCarousel';
import ProductGalleryThumbnails from './ProductGalleryThumbnails';
import type {
  GalleryProps as Props,
  GalleryState as State,
} from './product-gallery.types';
import { applyModifiers } from '../../utils';

const STYLE_PREFIX = 'c-product-gallery';

// Mapping for calculating responsive carousel options
const carouselBreakpointMap = [
  {
    breakpoint: 'large',
    slidesToShow: 2,
  },
  {
    breakpoint: 'xlarge',
    slidesToShow: 3,
  },
  {
    breakpoint: 'max',
    slidesToShow: 4,
  },
];

class ProductGallery extends Component<Props, State> {
  // ref to the carousel components
  featureCarousel: ?Carousel;
  thumbnailCarousel: ?Carousel;
  static defaultProps = {
    figureDimensions: [],
    getImageDimensions: () => {},
    getThumbnailWidth: () => {},
    images: [],
    onFeaturedImageClick: () => {},
    onZoomedImageClick: () => {},
    onImageResize: () => {},
  };

  /**
   * Tracking whether zoomed image and zoom tag are displayed
   * as well as initial click position to activate zoom view
   * @param {*} props
   */
  constructor(props: Props) {
    super(props);

    this.state = {
      isTouchDevice: false,
      receivedTouch: false,
      showZoomTag: true,
      zoomFocalPoint: [0, 0],
      showBaseImageCarousel: false,
      vertical: false,
      product: null,
      selectedSku: null,
      savedItemsMap: {},
      onSaveItemPress: () => {},
      currentImageIndex: 0,
      onImageResize: () => {},
      currency: null,
    };
  }

  /**
   * Do we need to display the thumbnails as a carousel on the initial render?
   */
  componentWillMount() {
    this.measureTotalThumbnailWidth(this.props.images);
  }

  /**
   * Fade out "click to zoom" tag
   */
  componentDidMount() {
    this.zoomTimeout = setTimeout(this.hideZoomTag, 3000);
    if (this.featureCarousel) {
      this.featureCarousel.resetSlider();
    }
    if (this.thumbnailCarousel) {
      this.thumbnailCarousel.resetSlider();
    }
  }

  /**
   * 1) Get the image aspect ratio from the first image that loads
   * 2) Thumnbnails - they need to know whether to render a carousel or not.
   * When using react-measure this wasn't a problem on component mount, but
   * the client-side changes kicked off by swatch selection did not prompt a
   * re-measure. This lead to inappropriate carousels (or missing carousels).
   * Here we check the total thumbnail width each time the `images` prop length
   * changes
   */
  componentWillReceiveProps({ figureDimensions, images }: Props) {
    if (
      !this.state.aspectRatio &&
      figureDimensions &&
      figureDimensions.length
    ) {
      this.setState({ aspectRatio: figureDimensions[1] / figureDimensions[0] });
    }
    if (images.length !== this.props.images.length) {
      this.measureTotalThumbnailWidth(images);
    }
  }

  /**
   * Carousel behaviour on swatch change - Any carousels in this
   * component need to reset to slide 0 on any change in the images prop.
   * TODO: there is an edge case in `react-slick` where a change of children
   * from a higher to lower amount will result in the carousel not resetting
   * to zero. We should probably rewrite the carousel component (removing
   * `react-slick`) to avoid this.
   *
   * @param {object} prevProps
   */
  componentDidUpdate(prevProps: Props) {
    if (
      prevProps.images[0] &&
      prevProps.images[0].id !== this.props.images[0].id
    ) {
      if (this.featureCarousel) {
        this.featureCarousel.resetSlider();
      }
      if (this.thumbnailCarousel) {
        this.thumbnailCarousel.resetSlider();
      }
    }
  }

  /**
   * Clear zoom tag timeout in case we leave the page before 3 seconds pass
   */
  componentWillUnmount() {
    clearTimeout(this.zoomTimeout);
  }

  zoomTimeout = null;

  hideZoomTag = () => this.setState({ showZoomTag: false });

  handleTouchMove = () => {
    // Using multiple properties here to handle touchscreen laptops
    this.setState({
      isTouchDevice: true,
      receivedTouch: true,
    });
  };

  /**
   * Update state on carousel init
   */
  onBaseImageCarouselInitialised = () => {
    this.setState({ showBaseImageCarousel: true });
  };

  handleFeaturedImageClick = (e: any) => {
    if (!this.props.isZoomed) {
      e.preventDefault();
      const {
        nativeEvent: { offsetX, offsetY },
      } = e;
      this.setState({ zoomFocalPoint: [offsetX, offsetY] });
      this.props.onFeaturedImageClick();
      setTimeout(() => this.setState({ isTouchDevice: false }), 500);
    }
  };

  setFeatureCarouselRef = (ref: ?Carousel) => {
    this.featureCarousel = ref;
  };

  setThumbnailCarouselRef = (ref: ?Carousel) => {
    this.thumbnailCarousel = ref;
  };

  /**
   * We need to see if we have enough room in the product
   * gallery to display all associated thumbnails. This will
   * drive the `displayCarousel` prop.
   * A thumbnail width reference is now set in the worker, and we should
   * assume that they are uniform
   * @param {Object[]} items
   */
  measureTotalThumbnailWidth = (items: Array<ContentfulImage> = []) => {
    const { thumbnailWidth } = items[0];
    this.props.getThumbnailWidth((thumbnailWidth || 0) * items.length);
  };

  /**
   * Renders.
   * @returns {*}
   */
  render() {
    const {
      isZoomed,
      breakpoints,
      isMobile,
      isTablet,
      images,
      displayCarousel,
      getThumbnailWidth,
      productGalleryContainers,
      selectedSku,
      product,
      savedItemsMap,
      onSaveItemPress,
      locale,
      onImageResize,
      currency,
    } = this.props;

    const {
      showBaseImageCarousel,
      receivedTouch,
      zoomFocalPoint,
      isTouchDevice,
      aspectRatio,
      showZoomTag,
    } = this.state;

    let setMainImage;

    const thumbnailCarouselOptions = {
      labels: { next: { heading: '' }, prev: { heading: '' } },
      responsive: [],
      slidesToShow: 4,
      slidesToScroll: !isMobile && !isTablet ? 4 : 1,
      vertical: !isMobile && !isTablet,
      infinite: false,
    };

    const baseImageCarouselOptions = {
      ...thumbnailCarouselOptions,
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: !isMobile && !isTablet,
      infinite: false,
      variableWidth: !isMobile && images.length === 1,
    };

    if (this.featureCarousel) {
      setMainImage = this.featureCarousel.onPaginationPress;
    }

    thumbnailCarouselOptions.responsive = carouselBreakpointMap.map(
      ({ breakpoint, slidesToShow }) => ({
        breakpoint: breakpoints[breakpoint],
        settings: {
          slidesToShow,
        },
      })
    );

    baseImageCarouselOptions.swipe = isTouchDevice;

    let baseStyle = 'displayed';
    if (isZoomed) baseStyle = 'hidden';

    let modifiers = [];

    if (images.length === 1) {
      modifiers = ['single-item'];
    }

    if (images.length > 1) {
      modifiers = ['multiple-items'];
    }

    if (showBaseImageCarousel) {
      modifiers.push('initialised');
    }

    const className = applyModifiers(STYLE_PREFIX, modifiers);

    const selectedSkuCurrent =
      selectedSku &&
      ((selectedSku.skuCode &&
        selectedSku.skuCode.indexOf(product.styleColourCode) !== -1) ||
        (typeof selectedSku === 'string' &&
          selectedSku.indexOf(product.styleColourCode) !== -1))
        ? selectedSku
        : null;

    const featureProps = {
      ...this.props,
      baseImageCarouselOptions,
      baseStyle,
      receivedTouch,
      isTouchDevice,
      aspectRatio,
      showZoomTag,
      initialFocalPoint: zoomFocalPoint,
      setRefOnParent: this.setFeatureCarouselRef,
      onCarouselInitialised: this.onBaseImageCarouselInitialised,
      handleTouchMove: this.handleTouchMove,
      handleFeaturedImageClick: this.handleFeaturedImageClick,
      selectedSkuCurrent,
      savedItemsMap,
      onSaveItemPress,
      arrows: !isMobile && !isTablet,
    };

    const thumbnailProps = {
      thumbnailCarouselOptions,
      images,
      setMainImage,
      displayCarousel,
      getThumbnailWidth,
      setRefOnParent: this.setThumbnailCarouselRef,
      infinite: false,
      currentImageIndex: this.state.currentImageIndex,
      arrows: true,
      imageIndexToHighlight: 0,
      isForMobile: false,
    };

    const thumbnailPropsMobile = {
      ...thumbnailProps,
      thumbnailCarouselOptions: {
        slidesToScroll: 1,
        slidesToShow: 4,
        infinite: false,
      },
      vertical: false,
      isForMobile: true,
      arrows: false,
    };

    const isDesktop = !isMobile && !isTablet;

    let containerClasses = '';
    containerClasses += isDesktop
      ? 'c-product-gallery__sticky-container--fit-content '
      : '';
    containerClasses +=
      !isMobile && locale === 'en-GB'
        ? 'c-product-gallery__sticky-container--uk'
        : '';
    containerClasses +=
      !isMobile && locale === 'en-US'
        ? 'c-product-gallery__sticky-container--us'
        : '';

    let galleryClasses = `${className} `;
    galleryClasses += isDesktop ? 'c-product-gallery--vertical ' : '';
    galleryClasses +=
      isDesktop && locale === 'en-GB'
        ? 'c-product-gallery--vertical-no-sticky'
        : '';

    return (
      <div className={containerClasses}>
        <div id={'product-gallery'} className={galleryClasses}>
          {
            <ProductGalleryMainCarousel
              {...featureProps}
              onCurrentImageChange={imageIndex =>
                this.setState({ currentImageIndex: imageIndex })
              }
              onImageResize={height => onImageResize(height)}
              currency={currency}
            />
          }
          {isDesktop && <ProductGalleryThumbnails {...thumbnailProps} />}
          {!isDesktop && <ProductGalleryThumbnails {...thumbnailPropsMobile} />}
        </div>
        {productGalleryContainers && !!productGalleryContainers.length && (
          <EditorialContainer containers={productGalleryContainers} />
        )}
      </div>
    );
  }
}

export default ProductGallery;
